/**
 * Dropping OS files onto a target — the whole-window upload overlay and the
 * create modal's drop zone both use this.
 *
 * They were separate copies of the same algorithm and had drifted into two
 * different bugs: the window one counted every `dragleave` (so an internal row
 * drag drove the counter negative and killed the overlay for the session), the
 * modal one clamped at zero but still closed early on an internal drag. Both
 * are gone here, because the rule is stated once.
 *
 * The counter is the crux. `dragenter`/`dragleave` fire for EVERY element the
 * pointer crosses, so a boolean would flicker off the moment the pointer moved
 * onto a child. Counting works only if both edges count the same events —
 * hence a single `isFileDrag` predicate guarding enter AND leave, and a clamp
 * at zero for the leaves the browser delivers without a matching enter (a drag
 * that began outside the window, or entered while the target was disabled).
 */

import { useCallback, useRef, useState } from 'react';
import { filenameProblem } from '@/shared/utils/filenames.js';

const MB = 1024 * 1024;

/** Only OS file drags count — an internal row drag carries no 'Files' type. */
const isFileDrag = (event) => Boolean(event.dataTransfer?.types?.includes?.('Files'));

/**
 * The project's upload limits as the browser checks them, from
 * GET /embed/v1/project/policies → `upload` (console → Settings; the backend
 * enforces them in `check_upload`). `maxMb` null: unknown — the server
 * decides. `allowedTypes` empty: every type.
 */
export function uploadLimits(policies) {
  const upload = policies?.upload || {};
  const maxMb = Number(upload.maxFileSizeMb ?? upload.max_file_size_mb) || null;
  const types = upload.allowedFileTypes ?? upload.allowed_file_types ?? [];
  return {
    maxMb,
    maxBytes: maxMb ? maxMb * MB : null,
    allowedTypes: types.map((type) => String(type).toLowerCase().replace(/^\./, '')),
  };
}

const extensionOf = (name) => {
  const dot = (name || '').lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

/**
 * Why one picked file can't be uploaded, by the same rules the server
 * applies: `{kind: 'too_large' | 'type_not_allowed' | 'name', reason?, ext?}`,
 * or null when it can.
 */
export function uploadProblem(file, limits = {}) {
  const nameProblem = filenameProblem(file?.name);
  if (nameProblem) return { kind: 'name', reason: nameProblem };
  if (limits.maxBytes && file.size > limits.maxBytes) return { kind: 'too_large' };
  const ext = extensionOf(file.name);
  if (limits.allowedTypes?.length && !limits.allowedTypes.includes(ext)) return { kind: 'type_not_allowed', ext };
  return null;
}

/**
 * Drop oversized files, reporting each one. Shared with the file-input path so
 * picking a file and dropping it enforce the same ceiling. No ceiling (null)
 * keeps every file: the project's limit is checked where the files land.
 */
export function withinSizeLimit(files, onTooLarge, maxBytes = null) {
  return Array.from(files).filter((file) => {
    if (maxBytes && file.size > maxBytes) {
      onTooLarge?.(file);
      return false;
    }
    return true;
  });
}

/**
 * @param onFiles    receives the dropped files that passed the size check
 * @param onTooLarge called once per oversized file, to report it however the
 *                   host prefers (the two call sites word it differently)
 * @param disabled   stop reacting entirely — a modal is covering the target
 * @param maxBytes   the per-file ceiling (the project's), or null for none
 * @returns `{ isDragOver, handlers }`, where `handlers` spreads onto an element
 *          or is attached to `window` one listener at a time
 */
export function useFileDrop({ onFiles, onTooLarge, disabled = false, maxBytes = null } = {}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsDragOver(false);
  }, []);

  const onDragEnter = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !isFileDrag(event)) return;
      depth.current += 1;
      if (depth.current === 1) setIsDragOver(true);
    },
    [disabled],
  );

  // Gated identically to onDragEnter. This symmetry IS the fix.
  const onDragLeave = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !isFileDrag(event)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsDragOver(false);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !isFileDrag(event)) return;
      // Without this the cursor shows "move" and some browsers refuse the drop.
      event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      // Always reset, even when disabled: the drag is over either way, and a
      // counter left standing would keep the overlay up.
      reset();
      if (disabled) return;

      const dropped = Array.from(event.dataTransfer?.files ?? []);
      if (!dropped.length) return;

      const accepted = withinSizeLimit(dropped, onTooLarge, maxBytes);
      if (accepted.length) onFiles?.(accepted);
    },
    [disabled, onFiles, onTooLarge, reset, maxBytes],
  );

  return {
    isDragOver,
    reset,
    handlers: { onDragEnter, onDragLeave, onDragOver, onDrop },
  };
}
